import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASONS = [
    { value: "spam", label: "Spam or unsolicited promotion" },
    { value: "harassment", label: "Harassment or bullying" },
    { value: "hate", label: "Hate speech or discrimination" },
    { value: "violence", label: "Violence or unsafe content" },
    { value: "other", label: "Other" },
];

/**
 * Type-to-confirm-style report submission for shared conversations. Works
 * for both anonymous viewers and signed-in users — the backend resolves
 * `reporter_user_id` from an optional bearer token. Idempotency by
 * (share_token, reporter_ip) within 1h is enforced server-side.
 */
export default function ReportConversationDialog({ open, onOpenChange, shareToken }) {
    const [reason, setReason] = useState("spam");
    const [details, setDetails] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setReason("spam");
        setDetails("");
    };

    const handleSubmit = async () => {
        if (busy || !shareToken) return;
        setBusy(true);
        try {
            await axios.post(`${API}/share/${shareToken}/report`, {
                reason,
                details: details.trim() || undefined,
            });
            toast.success("Thank you. We'll review this within 24 hours.");
            reset();
            onOpenChange(false);
        } catch (e) {
            console.warn("[report] failed", e?.response?.status);
            toast.error("Could not submit report. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const onOpenChangeWrapped = (next) => {
        if (!next) reset();
        onOpenChange(next);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChangeWrapped}>
            <DialogContent
                className="max-w-md border-[#E5E5E5] bg-white text-[#0A0A0A]"
                data-testid="report-conversation-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
                        <Flag size={14} strokeWidth={2.25} />
                        Report this conversation
                    </DialogTitle>
                    <DialogDescription className="text-[13px] leading-relaxed text-[#525252]">
                        Help keep Piqpath safe. Your report is anonymous to the conversation
                        owner; our moderation team reviews each report within 24 hours.
                    </DialogDescription>
                </DialogHeader>

                <fieldset
                    className="mt-1 space-y-1.5"
                    role="radiogroup"
                    aria-label="Report reason"
                    data-testid="report-reason-group"
                >
                    {REASONS.map((r) => {
                        const active = reason === r.value;
                        return (
                            <label
                                key={r.value}
                                className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-[13px] transition ${
                                    active
                                        ? "border-[#0A0A0A] bg-[#F5F5F5]"
                                        : "border-[#E5E5E5] bg-white hover:border-[#0A0A0A]"
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="report-reason"
                                    value={r.value}
                                    checked={active}
                                    onChange={() => setReason(r.value)}
                                    data-testid={`report-reason-radio-${r.value}`}
                                    className="sr-only"
                                />
                                <span
                                    className={`inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border ${
                                        active ? "border-[#0A0A0A]" : "border-[#BFBFBF]"
                                    }`}
                                >
                                    {active && <span className="h-1.5 w-1.5 rounded-full bg-[#0A0A0A]" />}
                                </span>
                                <span className="text-[#0A0A0A]">{r.label}</span>
                            </label>
                        );
                    })}
                </fieldset>

                <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value.slice(0, 500))}
                    placeholder="Add details (optional, max 500 chars)"
                    rows={3}
                    data-testid="report-details-input"
                    className="mt-2 w-full resize-none rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[13px] text-[#0A0A0A] outline-none transition focus:border-[#0A0A0A]"
                />
                <div className="text-right text-[11px] text-[#A3A3A3]">{details.length} / 500</div>

                <DialogFooter className="mt-2 flex gap-2 sm:gap-2">
                    <button
                        type="button"
                        onClick={() => onOpenChangeWrapped(false)}
                        data-testid="report-cancel-button"
                        className="rounded-lg border border-[#E5E5E5] bg-white px-4 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={busy}
                        data-testid="report-submit-button"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#262626] disabled:cursor-not-allowed disabled:bg-[#A3A3A3]"
                    >
                        {busy ? "Submitting…" : "Submit report"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
